// src/hooks/useOwnAttendance.js — "have I marked today, and may I?" asked once.
//
// Two surfaces ask it: MyAttendance (the agent's own card, on the attendance tab) and
// ShiftPrompt (the shell banner that appears when their shift starts). They must never give
// different answers — a banner saying "you have not arrived" over a card saying "arrived 09:12"
// is the kind of contradiction this codebase keeps paying for — so the fetching, the refusal
// vocabulary and the two acts live here and the components own only their layout.
//
// ⚠️ NOTHING HERE IS ENFORCEMENT. mark_own_arrival / mark_own_departure resolve the agent from
// the SESSION and the location from the EDGE (0002), and attendance writes are management-only
// (0003), so a lie told by this file changes nothing in the database. What it owns is telling
// the truth BEFORE the press — a control that refuses on click, with no way to know beforehand,
// is the "lock naming an action they do not have" complaint this app keeps recording.
import { useState, useEffect, useCallback } from 'react'
import { markOwnArrival, markOwnDeparture, checkAttendanceLocation, getAttendance } from '../db/index.js'
import { todayStr } from '../lib/clock.js'
import { t } from '../lib/i18n.js'

const hhmm = v => (v ? String(v).slice(0, 5) : null)

export function useOwnAttendance(agentId) {
  const [record, setRecord] = useState(null)   // null = loading · false = the read failed
  const [loc,    setLoc]    = useState(null)   // the location verdict, or null while asking
  const [busy,   setBusy]   = useState(false)
  const [err,    setErr]    = useState('')
  const [ver,    setVer]    = useState(0)
  const date = todayStr()

  useEffect(() => {
    if (!agentId) return
    let gone = false
    getAttendance({ agentId, dateFrom: date, dateTo: date })
      .then(rows => { if (!gone) setRecord((rows || [])[0] || {}) })
      // ⚠️ A failed read is UNKNOWN, never "you have not arrived": offering the arrival button
      // over a row that already exists is how a second press comes to look necessary.
      .catch(() => { if (!gone) setRecord(false) })
    return () => { gone = true }
  }, [agentId, date, ver])

  // Asked on open and re-asked after each act, because an agent walks out mid-session and the
  // answer stops being true the moment they do. Deliberately NOT on a timer: this runs in every
  // agent's browser all day, and a background poll per person is a workload nobody owns.
  useEffect(() => {
    if (!agentId) return
    let gone = false
    checkAttendanceLocation(agentId)
      .then(r => { if (!gone) setLoc(r || null) })
      .catch(() => { if (!gone) setLoc(null) })
    return () => { gone = true }
  }, [agentId, ver])

  const refresh = useCallback(() => setVer(v => v + 1), [])

  const run = useCallback(async (fn) => {
    if (busy) return null
    setBusy(true); setErr('')
    try {
      const r = await fn()
      if (r && r.ok === false) {
        // A known reason gets its sentence; an unknown one is shown raw rather than swallowed,
        // because a refusal nobody can read is indistinguishable from nothing happening.
        const key = `my_att_refused_${r.reason}`
        setErr(t(key) !== key ? t(key) : String(r.reason || ''))
      }
      setVer(v => v + 1)
      return r
    } catch (e) { setErr(e.message); return null } finally { setBusy(false) }
  }, [busy])

  const arrive = useCallback(() => run(markOwnArrival), [run])
  const leave  = useCallback(() => run(markOwnDeparture), [run])

  return {
    date,
    record,
    loading: record === null,
    failed: record === false,
    checkIn:  hhmm(record && record.check_in),
    checkOut: hhmm(record && record.check_out),
    loc,
    // The one place that decides whether pressing "I came" can succeed. `loc.ok !== false` and
    // not `loc?.ok === true`: a verdict that has not arrived must not disable the button, or a
    // slow query would look like a refusal.
    canArrive: !hhmm(record && record.check_in) && loc?.ok !== false,
    busy, err, arrive, leave, refresh,
  }
}
